import type { FileRejections } from "@v2/features/products/utils";

import * as React from "react";

import { handleFileRejections } from "@v2/features/products/utils";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { UploadCanceledError } from "@v2/utils/cloudflareUpload";
import { useDropzone } from "react-dropzone";
import { ReploError } from "schemas/errors";
import {
  MAX_FILE_UPLOAD_COUNT,
  MAX_PRODUCT_FILE_SIZE_BYTES,
} from "schemas/productFile";
import { v4 } from "uuid";

import { useProductFileUpload } from "./useProductFileUpload";

type CommonProductFileDropZoneProps = {
  onDropBeforeUpload?: (id: string) => string | null;
  onUploadComplete: ({
    url,
    file,
    id,
  }: {
    url: string;
    file?: File;
    id?: string;
  }) => void;
  onError?: (id: string) => void;
  acceptDropAssetType: string[] | null | undefined;
  allowClickToUpload: boolean;
  isDisabled?: boolean;
  currentFiles?: Array<{ name: string; fileExtension: string }>;
};

export function useReploProductFileDropZone(
  props: CommonProductFileDropZoneProps,
) {
  const { uploadProductFile, isUploading, progressByFileId, cancelUpload } =
    useProductFileUpload();
  const result = useProductFileDropZone({
    ...props,
    uploadFile: async ({ id, file }) => {
      const pendingFile = await uploadProductFile({ fileId: id, file });
      return pendingFile.url;
    },
  });
  return { ...result, isUploading, progressByFileId, cancelUpload };
}

function useProductFileDropZone({
  onDropBeforeUpload,
  onUploadComplete,
  onError,
  acceptDropAssetType,
  allowClickToUpload,
  isDisabled = false,
  uploadFile,
  currentFiles = [],
}: CommonProductFileDropZoneProps & {
  uploadFile: (args: { file: File; id: string }) => Promise<string>;
}) {
  const [droppedFilesMap, setDroppedFilesMap] = React.useState<
    Map<string, File>
  >(new Map());

  const handleError = React.useCallback(
    ({ id }: { id: string }) => {
      setDroppedFilesMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
      onError?.(id);
    },
    [onError],
  );

  const errorToast = useErrorToast();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
    acceptedFiles,
  } = useDropzone({
    maxSize: MAX_PRODUCT_FILE_SIZE_BYTES,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejections) => {
      const shouldStopProcessing = handleFileRejections(
        fileRejections,
        errorToast,
      );
      if (shouldStopProcessing) {
        return;
      }

      const existingFilenames = new Set([
        ...currentFiles.map((file) => `${file.name}.${file.fileExtension}`),
        ...Array.from(droppedFilesMap.values()).map((file) => file.name),
      ]);

      const duplicateFiles = acceptedFiles.filter((file) => {
        return existingFilenames.has(file.name);
      });

      const uniqueFiles = acceptedFiles.filter(
        (file) => !existingFilenames.has(file.name),
      );

      if (duplicateFiles.length > 0) {
        const duplicateNames = duplicateFiles
          .map((file) => file.name)
          .join("\n");
        errorToast(
          "Duplicate Files",
          `You already uploaded ${duplicateFiles.length === 1 ? "this file" : "these files"}: ${duplicateNames}. Please choose different files or rename them.`,
          "error.file.upload",
        );
      }

      if (uniqueFiles.length === 0) {
        return;
      }

      const internalDroppedFilesMap = new Map(
        uniqueFiles.map((file) => [v4(), file]),
      );
      setDroppedFilesMap(internalDroppedFilesMap);

      const uploadPromises = Array.from(internalDroppedFilesMap.entries()).map(
        async ([id, file]) => {
          try {
            onDropBeforeUpload?.(id);

            const url = await uploadFile({ file, id });
            onUploadComplete({
              url,
              file,
              id,
            });
            setDroppedFilesMap((prev) => {
              const newMap = new Map(prev);
              newMap.delete(id);
              return newMap;
            });
          } catch (error) {
            if (!(error instanceof UploadCanceledError)) {
              errorToast(
                "Error Uploading File",
                "An error occurred while uploading the product file. Please try again.",
                "error.file.upload",
                {
                  error:
                    error instanceof ReploError ? error.message : String(error),
                },
              );
            }
            handleError({ id });
          }
        },
      );

      void Promise.all(uploadPromises);
    },
    accept: acceptDropAssetType ?? undefined,
    maxFiles: MAX_FILE_UPLOAD_COUNT,
    noClick: !allowClickToUpload,
    noKeyboard: true,
    disabled: isDisabled,
  });

  return {
    getRootProps,
    getInputProps,
    openFileDialog,
    isDragActive,
    droppedFilesMap,
    acceptedFiles,
  };
}
